import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reservationsApi, tablesApi } from '../services/api';
import {
  Plus,
  Calendar,
  Clock,
  Users,
  Phone,
  Check,
  X,
  Edit2,
  AlertTriangle,
  Star,
  MapPin,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import { format, addDays, subDays, startOfToday, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay, isToday, parseISO } from 'date-fns';
import clsx from 'clsx';

interface Reservation {
  id: string;
  guestId?: string;
  guestName: string;
  guestPhone?: string;
  guestEmail?: string;
  partySize: number;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'seated' | 'completed' | 'cancelled';
  tableIds?: string[];
  tableId?: string;
  tableName?: string;
  notes?: string;
  specialRequests?: string;
  dietaryNotes?: string;
  seatedAt?: string;
  departedAt?: string;
  guest?: {
    id: string;
    firstName: string;
    lastName: string;
    allergies?: string[];
    dietaryRestrictions?: string[];
    isVIP?: boolean;
    notes?: string;
  };
}

const defaultFormData = {
  guestName: '',
  guestPhone: '',
  guestEmail: '',
  partySize: 2,
  date: format(startOfToday(), 'yyyy-MM-dd'),
  time: '18:00',
  notes: '',
};

export default function ReservationsPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(format(startOfToday(), 'yyyy-MM-dd'));
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [formError, setFormError] = useState('');

  const [showPast, setShowPast] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Generate date options: past 7 days or next 7 days
  const dateOptions = showPast
    ? Array.from({ length: 7 }, (_, i) => {
        const date = subDays(startOfToday(), 7 - i);
        return {
          value: format(date, 'yyyy-MM-dd'),
          label: format(date, 'EEE, MMM d'),
        };
      })
    : Array.from({ length: 7 }, (_, i) => {
        const date = addDays(startOfToday(), i);
        return {
          value: format(date, 'yyyy-MM-dd'),
          label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : format(date, 'EEE, MMM d'),
        };
      });

  // Fetch reservations
  const { data: reservationsData, isLoading } = useQuery({
    queryKey: ['reservations', selectedDate],
    queryFn: async () => {
      const response = await reservationsApi.getAll({ date: selectedDate });
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch tables for seating
  const { data: tablesData } = useQuery({
    queryKey: ['tables'],
    queryFn: async () => {
      const response = await tablesApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Use real data only - no mock fallbacks
  const reservations = Array.isArray(reservationsData) ? reservationsData : [];
  const tables = Array.isArray(tablesData) ? tablesData : [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => reservationsApi.create(data),
    onSuccess: (_data, variables) => {
      // Switch to the date of the newly created reservation so it shows up
      if (variables.date && variables.date !== selectedDate) {
        setSelectedDate(variables.date);
        // Also update showPast based on the reservation date
        const reservationDate = parseISO(variables.date);
        const today = startOfToday();
        setShowPast(reservationDate < today);
      }
      // Comprehensive invalidation for cross-view consistency
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
      setShowAddModal(false);
      setFormData(defaultFormData);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create reservation');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      reservationsApi.update(id, data),
    onSuccess: () => {
      // Comprehensive invalidation for cross-view consistency
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
      setEditingReservation(null);
      setFormData(defaultFormData);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update reservation');
    },
  });

  // Confirm mutation
  const confirmMutation = useMutation({
    mutationFn: (id: string) => reservationsApi.confirm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => reservationsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
    },
  });

  // Seat mutation - comprehensive invalidation for cross-view consistency
  const seatMutation = useMutation({
    mutationFn: ({ id, tableId }: { id: string; tableId: string }) =>
      reservationsApi.seat(id, tableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      // Invalidate guests since seating updates visit count
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });

  const filteredReservations = reservations.filter((res: Reservation) => {
    if (selectedStatus === 'all') return true;
    return res.status === selectedStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-success-100 text-success-700';
      case 'pending': return 'bg-warning-100 text-warning-700';
      case 'seated': return 'bg-primary-100 text-primary-700';
      case 'completed': return 'bg-slate-100 text-slate-700';
      case 'cancelled': return 'bg-danger-100 text-danger-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const handleSeat = (reservation: Reservation) => {
    const availableTables = tables.filter((t: any) => t.status === 'available');
    if (availableTables.length === 0) {
      alert('No available tables');
      return;
    }
    const tableId = prompt(`Enter table ID to seat. Available: ${availableTables.map((t: any) => t.number).join(', ')}`);
    if (tableId) {
      const table = availableTables.find((t: any) => t.number?.toString() === tableId || t.id === tableId);
      if (table) {
        seatMutation.mutate({ id: reservation.id, tableId: table.id });
      }
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reservations</h1>
            <p className="text-slate-500">Manage upcoming reservations</p>
          </div>
          <HelpButton featureId="reservations" />
        </div>
        <button
          onClick={() => {
            // Default to the currently selected date, not today
            setFormData({ ...defaultFormData, date: selectedDate });
            setShowAddModal(true);
          }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Reservation
        </button>
      </div>

      {/* Past/Upcoming Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => {
              setShowPast(false);
              setSelectedDate(format(startOfToday(), 'yyyy-MM-dd'));
            }}
            className={clsx(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
              !showPast ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            )}
          >
            Upcoming
          </button>
          <button
            onClick={() => {
              setShowPast(true);
              setSelectedDate(format(subDays(startOfToday(), 1), 'yyyy-MM-dd'));
            }}
            className={clsx(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
              showPast ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            )}
          >
            Past
          </button>
        </div>
      </div>

      {/* Date Selection */}
      <div className="flex gap-2 items-center">
        <div className="flex gap-2 overflow-x-auto pb-2 items-center flex-1">
          {dateOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedDate(option.value)}
              className={clsx(
                'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors',
                selectedDate === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              )}
            >
              {option.label}
            </button>
          ))}

          {/* Selected Date Display */}
          {!dateOptions.some(opt => opt.value === selectedDate) && (
            <span className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg font-medium whitespace-nowrap">
              {format(parseISO(selectedDate), 'EEE, MMM d, yyyy')}
            </span>
          )}
        </div>

        {/* Calendar Picker Button - outside overflow container */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => {
              setCalendarMonth(parseISO(selectedDate));
              setShowCalendar(!showCalendar);
            }}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
              showCalendar
                ? 'bg-primary-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            )}
          >
            <Calendar className="w-4 h-4" />
            Pick Date
          </button>

          {/* Calendar Popup */}
          {showCalendar && (
            <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 w-80">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold text-lg">
                  {format(calendarMonth, 'MMMM yyyy')}
                </h3>
                <button
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const monthStart = startOfMonth(calendarMonth);
                  const monthEnd = endOfMonth(calendarMonth);
                  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                  const startDay = getDay(monthStart);

                  // Add empty cells for days before the month starts
                  const emptyCells = Array(startDay).fill(null);

                  return (
                    <>
                      {emptyCells.map((_, i) => (
                        <div key={`empty-${i}`} className="w-9 h-9" />
                      ))}
                      {days.map((day) => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const isSelected = dateStr === selectedDate;
                        const isTodayDate = isToday(day);

                        return (
                          <button
                            key={dateStr}
                            onClick={() => {
                              setSelectedDate(dateStr);
                              setShowCalendar(false);
                              // Switch to past/upcoming based on selected date
                              const today = startOfToday();
                              setShowPast(day < today);
                            }}
                            className={clsx(
                              'w-9 h-9 rounded-lg text-sm font-medium transition-colors',
                              isSelected
                                ? 'bg-primary-600 text-white'
                                : isTodayDate
                                  ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                                  : 'hover:bg-slate-100 text-slate-700'
                            )}
                          >
                            {format(day, 'd')}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setSelectedDate(format(startOfToday(), 'yyyy-MM-dd'));
                    setShowCalendar(false);
                    setShowPast(false);
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={() => setShowCalendar(false)}
                  className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 overflow-x-auto">
        {['all', 'pending', 'confirmed', 'seated', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={clsx(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              selectedStatus === status
                ? 'bg-slate-800 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Reservations List */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <div className="space-y-3">
          {filteredReservations.map((reservation: Reservation) => {
            const allergies = reservation.guest?.allergies || [];
            const dietary = reservation.guest?.dietaryRestrictions || [];
            const hasAlerts = allergies.length > 0 || dietary.length > 0 || reservation.dietaryNotes;
            const isVIP = reservation.guest?.isVIP;

            return (
            <div key={reservation.id} className={clsx('card p-4', isVIP && 'border-amber-300 bg-amber-50/30')}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="text-center pt-1">
                    <Clock className="w-5 h-5 text-slate-400 mx-auto" />
                    <p className="font-bold text-lg">{reservation.time}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{reservation.guestName}</h3>
                      {isVIP && (
                        <span className="flex items-center gap-0.5 text-amber-600 text-xs font-medium">
                          <Star className="w-3 h-3 fill-amber-400" />
                          VIP
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {reservation.partySize} guests
                      </span>
                      {reservation.guestPhone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {reservation.guestPhone}
                        </span>
                      )}
                    </div>

                    {/* Allergies & Dietary Info */}
                    {hasAlerts && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {allergies.map((allergy: string, i: number) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            {allergy}
                          </span>
                        ))}
                        {dietary.map((diet: string, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                            {diet}
                          </span>
                        ))}
                        {reservation.dietaryNotes && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                            {reservation.dietaryNotes}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Special Requests */}
                    {reservation.specialRequests && (
                      <p className="text-sm text-slate-500 mt-1 italic">
                        "{reservation.specialRequests}"
                      </p>
                    )}

                    {/* Table Assignment */}
                    {reservation.tableIds && reservation.tableIds.length > 0 ? (
                      <div className="flex items-center gap-1 mt-2 text-sm text-primary-600">
                        <MapPin className="w-4 h-4" />
                        <span>Table {tables.filter((t: any) => reservation.tableIds?.includes(t.id)).map((t: any) => t.number || t.name).join(', ') || reservation.tableName}</span>
                      </div>
                    ) : reservation.tableName ? (
                      <p className="text-sm text-primary-600 mt-1">{reservation.tableName}</p>
                    ) : (reservation.status === 'pending' || reservation.status === 'confirmed') && (
                      <div className="mt-2">
                        <select
                          className="text-sm border border-slate-200 rounded px-2 py-1 bg-white"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              // Update reservation with table assignment
                              updateMutation.mutate({
                                id: reservation.id,
                                data: { ...formData, tableIds: [e.target.value] } as any
                              });
                            }
                          }}
                        >
                          <option value="">Assign table...</option>
                          {tables
                            .filter((t: any) => t.status === 'available' || t.capacity >= reservation.partySize)
                            .map((table: any) => (
                              <option key={table.id} value={table.id}>
                                {table.number || table.name} (seats {table.capacity})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    )}

                    {reservation.status === 'completed' && reservation.seatedAt && reservation.departedAt && (
                      <p className="text-sm text-slate-500 mt-1">
                        Visit: {Math.round((new Date(reservation.departedAt).getTime() - new Date(reservation.seatedAt).getTime()) / 60000)} min
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={clsx('badge', getStatusColor(reservation.status))}>
                    {reservation.status}
                  </span>

                  {/* Edit button - available for pending/confirmed reservations */}
                  {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                    <button
                      onClick={() => {
                        setEditingReservation(reservation);
                        setFormData({
                          guestName: reservation.guestName,
                          guestPhone: reservation.guestPhone || '',
                          guestEmail: reservation.guestEmail || '',
                          partySize: reservation.partySize,
                          date: reservation.date.split('T')[0],
                          time: reservation.time,
                          notes: reservation.notes || '',
                        });
                      }}
                      className="btn btn-sm btn-ghost"
                      title="Edit reservation"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}

                  {reservation.status === 'pending' && (
                    <button
                      onClick={() => confirmMutation.mutate(reservation.id)}
                      className="btn btn-sm btn-success"
                      disabled={confirmMutation.isPending}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}

                  {reservation.status === 'confirmed' && (
                    <button
                      onClick={() => handleSeat(reservation)}
                      className="btn btn-sm btn-primary"
                      disabled={seatMutation.isPending}
                    >
                      Seat
                    </button>
                  )}

                  {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                    <button
                      onClick={() => {
                        if (confirm('Cancel this reservation?')) {
                          cancelMutation.mutate(reservation.id);
                        }
                      }}
                      className="btn btn-sm btn-ghost text-danger-600"
                      disabled={cancelMutation.isPending}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {filteredReservations.length === 0 && !isLoading && (
        <div className="text-center py-12 text-slate-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No reservations found</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingReservation) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editingReservation ? 'Edit Reservation' : 'New Reservation'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingReservation(null);
                  setFormData(defaultFormData);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={(e) => {
              e.preventDefault();
              if (editingReservation) {
                updateMutation.mutate({ id: editingReservation.id, data: formData });
              } else {
                createMutation.mutate(formData);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Guest Name *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={formData.guestName}
                    onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      className="input w-full"
                      value={formData.guestPhone}
                      onChange={(e) => setFormData({ ...formData, guestPhone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="input w-full"
                      value={formData.guestEmail}
                      onChange={(e) => setFormData({ ...formData, guestEmail: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Party Size *</label>
                    <input
                      type="number"
                      min="1"
                      className="input w-full"
                      value={formData.partySize}
                      onChange={(e) => setFormData({ ...formData, partySize: parseInt(e.target.value) || 1 })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                    <input
                      type="date"
                      className="input w-full"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Time *</label>
                    <input
                      type="time"
                      className="input w-full"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    className="input w-full"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingReservation(null);
                    setFormData(defaultFormData);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingReservation
                    ? (updateMutation.isPending ? 'Saving...' : 'Save Changes')
                    : (createMutation.isPending ? 'Creating...' : 'Create Reservation')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
